import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, finalize, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { ILegacyList } from '@site-mate/dashpivot-shared-library';

import { ListsService } from 'app/lists/lists.service';

type ListDataMap = Map<string, ILegacyList>;
type ListIsLoadingMap = Map<string, boolean>;

export type IDryList = Pick<ILegacyList, '_id' | 'name'>;
@Injectable({ providedIn: 'root' })
export class TemplateListsService {
  private activeRequests = new Map<string, Observable<ILegacyList>>();
  private listDataMapSubject = new BehaviorSubject<ListDataMap>(new Map());
  private listIsLoadingMapSubject = new BehaviorSubject<ListIsLoadingMap>(new Map());
  private availableListsSubject = new BehaviorSubject<IDryList[]>([]);

  public listIsLoading$ = this.listIsLoadingMapSubject.asObservable();
  public listDataMap$ = this.listDataMapSubject.asObservable();
  public availableLists$ = this.availableListsSubject.asObservable();

  constructor(private readonly listsService: ListsService) {}

  get listDataMap(): Map<string, ILegacyList> {
    return this.listDataMapSubject.value;
  }
  get listIsLoadingMap(): Map<string, boolean> {
    return this.listIsLoadingMapSubject.value;
  }

  setAvailableLists(lists: IDryList[]) {
    this.availableListsSubject.next(lists);
  }

  getListData(listId: string) {
    if (!listId) {
      return of(null);
    }

    if (this.listDataMap.has(listId)) {
      return of(this.listDataMap.get(listId));
    }

    if (this.activeRequests.has(listId)) {
      return this.activeRequests.get(listId);
    }

    this.setListIsLoading(listId, true);

    const request$ = this.listsService.getList(listId).pipe(
      tap((list) => {
        const latestDataMapValue = this.listDataMap;
        latestDataMapValue.set(listId, list);
        this.listDataMapSubject.next(latestDataMapValue);
      }),
      catchError((error) => {
        this.listDataMap.set(listId, error);
        return throwError(() => error);
      }),
      shareReplay(1),
      finalize(() => {
        this.setListIsLoading(listId, false);
        this.activeRequests.delete(listId);
      }),
    );

    this.activeRequests.set(listId, request$);
    return request$;
  }

  emptyListData(listId: string) {
    this.listDataMap.delete(listId);
  }

  emptyAllListData() {
    this.listDataMap.clear();
    this.listIsLoadingMap.clear();
  }

  setListIsLoading(listId: string, isLoading: boolean) {
    this.listIsLoadingMap.set(listId, isLoading);
    this.listIsLoadingMapSubject.next(this.listIsLoadingMap);
  }
}
